import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/cn';
import { Spinner } from './spinner';

/**
 * Sizes are picked against the 44px minimum touch target in CONTRACT §8.
 * `md` and up clear it; `sm` exists for dense desktop toolbars and must not be
 * used for a primary action on a touch surface.
 */
export const buttonVariants = cva(
  [
    'relative inline-flex select-none items-center justify-center gap-2',
    'whitespace-nowrap rounded-lg font-medium',
    'transition-colors duration-150 ease-out',
    'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-ink hover:bg-accent/90 active:bg-accent/80',
        secondary:
          'border border-line bg-surface text-ink hover:bg-raised active:bg-raised/80',
        ghost: 'bg-transparent text-ink hover:bg-raised active:bg-raised/80',
        danger:
          'bg-status-missed text-white hover:bg-status-missed/90 active:bg-status-missed/80',
      },
      size: {
        sm: 'h-9 px-3 text-sm [&_svg]:size-4',
        md: 'h-11 px-4 text-sm [&_svg]:size-4',
        lg: 'h-13 px-6 text-base [&_svg]:size-5',
        icon: 'size-11 p-0 [&_svg]:size-5',
      },
      loading: {
        true: 'cursor-progress',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      loading: false,
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, 'loading'> {
  /**
   * Shows a spinner, disables the button and marks it aria-busy.
   *
   * This is not decoration: it is the thing that stops a second tap on "Take"
   * while the first request is still in flight. The client never retries a
   * mutation (see lib/api.ts) and must not let the user do it by accident.
   */
  loading?: boolean;
  /** Render as the child element (a Link, say) while keeping button styling. */
  asChild?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    loading = false,
    asChild = false,
    disabled,
    children,
    type,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      // Inside a form an untyped <button> submits. That has logged doses by
      // accident before; be explicit.
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(buttonVariants({ variant, size, loading }), className)}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
      {...props}
    >
      {loading ? (
        <>
          {/* The label stays laid out but invisible so the button does not
              change width mid-request and move under a thumb. */}
          <span className="inline-flex items-center gap-2 opacity-0">{children}</span>
          <span className="absolute inset-0 grid place-items-center">
            <Spinner size={size === 'lg' ? 'md' : 'sm'} />
          </span>
        </>
      ) : (
        children
      )}
    </Comp>
  );
});
